import { createApp, fetchApp, updateApp, updateAppPersonalChat } from '@/services/app'
import { createChannel, deleteChannel, fetchChannels, updateChannel } from '@/services/channels'
import { uploadMediaObject } from '@/services/media'

import {
  createPost,
  CreatePostRequest,
  deletePost,
  fetchPosts,
  updatePost,
  UpdatePostRequest,
} from '@/services/posts'
import {
  createPromotionalPage,
  createTariff,
  CreateTariffRequest,
  updateTariff,
  UpdateTariffRequest,
  updatePromotionalPage
} from '@/services/tariffs'
import {
  BuilderActions,
  BuilderDeleteChannelActionPayload,
  BuilderMutateChannelActionPayload,
  BuilderSetAppColorActionPayload,
  BuilderSetProfileAvatarActionPayload,
  BuilderSetProfileCoverActionPayload,
  BuilderSetProfileDescriptionActionPayload,
  BuilderSetProfileDomainNameActionPayload,
  BuilderSetProfileNameActionPayload,
  BuilderSetProfilePostsActionPayload,
  BuilderSetProfileSubscriptionsActionPayload,
} from './types'
import { mapPost, mapTariff, setInitialBuilderState, uploadPostAttachments } from './utils'
import {PromotionalPageRequest, PromotionalPageRequestUpdate} from "@/services/types"


const actions: BuilderActions = {

  async setApp({ commit }, id: number | null): Promise<void> {
    if (!id) {
      setInitialBuilderState(commit)
      return
    }
    
    const appId = Number(id)

    try {
      const [app, posts, channels] = await Promise.all([
        fetchApp(appId),
        fetchPosts({ appId }),
        fetchChannels({ appId }),
      ])

      commit('setApp', app)
      commit('setChannels', channels)

      commit('setProfile', {
        name: app.name,
        avatar: app.image,
        description: app.description,
        color: app.primaryColor,
        cover: app.headerImages,
        subscriptions: app.tariffs.map(mapTariff),
        posts: posts.map(mapPost),
      })
    } catch (err) {
      setInitialBuilderState(commit)

      throw err
    }
  },

  async setProfileAppColor(
    { commit, state },
    { color }: BuilderSetAppColorActionPayload
  ): Promise<void> {
    if (state.app) {
      await updateApp({ id: state.app.id, primaryColor: color })
    }

    commit('setProfile', { ...state.profile, color })
  },

  async setProfileName(
    { commit, state, rootGetters },
    { name }: BuilderSetProfileNameActionPayload
  ): Promise<void> {
    if (state.app) {
      const app = await updateApp({ id: state.app.id, name })
      commit('setApp', app)
      commit('setProfile', { ...state.profile, name })

      return
    }

    if (!state.profile.color) {
      throw new Error('Profile name and color are required!')
    }

    const currency = rootGetters['currency/getCurrency'].items.find(item => item.code === state.appCurrency)
    
    const app = await createApp({ name, primaryColor: state.profile.color, currency:currency.id })

    commit('setApp', app)
    commit('setProfile', { ...state.profile, name })
  },

  async setProfileDomainName(
    { commit, state },
    { domainName }: BuilderSetProfileDomainNameActionPayload
  ): Promise<void> {
    commit('setProfile', { ...state.profile, domainName })
  },

  async setProfileAvatar(
    { commit, state },
    { avatar }: BuilderSetProfileAvatarActionPayload
  ): Promise<void> {
    if (!state.app) {
      return
    }

    const image = await uploadMediaObject(avatar)
    const app = await updateApp({ id: state.app.id, image })

    commit('setApp', app)
    commit('setProfile', { ...state.profile, avatar: app.image })
  },

  async setProfileDescription(
    { commit, state },
    { description }: BuilderSetProfileDescriptionActionPayload
  ): Promise<void> {
    if (!state.app) {
      return
    }
    const app = await updateApp({ id: state.app.id, description })

    commit('setApp', app)
    commit('setProfile', { ...state.profile, description })
  },

  async setProfileCover(
    { commit, state },
    { cover }: BuilderSetProfileCoverActionPayload
  ): Promise<void> {
    if (!state.app) {
      return
    }

    const toUpload: File[] = []
    const ids: number[] = []

    cover.forEach((v) => (v instanceof File ? toUpload.push(v) : ids.push(v)))
    const uploadedImages = await Promise.all(toUpload.map((f) => uploadMediaObject(f)))

    const app = await updateApp({
      id: state.app.id,
      headerImageIds: [...uploadedImages.map((v) => v.id), ...ids],
    })

    commit('setApp', app)
    commit('setProfile', { ...state.profile, cover: app.headerImages })
  },

  async setProfileSubscriptions(
    { commit, state },
    { subscriptions }: BuilderSetProfileSubscriptionsActionPayload
  ): Promise<void> {
    if (!state.app) {
      return
    }

    const toCreate: CreateTariffRequest[] = []
    const toUpdate: UpdateTariffRequest[] = []

    subscriptions.forEach((s) => {
      if (s.id > 0) {
        toUpdate.push({
          id: s.id,
          name: s.name,
          type: s.duration,
          amount: s.price,
          period: s.frequency,
        })
      } else if (state.app) {
        toCreate.push({
          appId: state.app.id,
          name: s.name,
          type: s.duration,
          amount: s.price,
          period: s.frequency,
        })
      }
    })
    
    const tariffs : any[] = []
    for await (const r of toCreate) { 
      tariffs.push(await createTariff(r))
    }
    for await (const r of toUpdate) {
      tariffs.push(await updateTariff(r))
    }

    const app = await updateApp({
      id: state.app.id,
      tariffIds: tariffs.map((t) => t.id),
    })

    commit('setApp', app)
    commit('setProfile', { ...state.profile, subscriptions: tariffs.map(mapTariff) })
  },

  async setProfilePosts(
    { commit, state },
    { posts }: BuilderSetProfilePostsActionPayload
  ): Promise<void> {
    if (!state.app) {
      return
    }

    const attachments = await uploadPostAttachments({ posts })

    const toCreate: CreatePostRequest[] = []
    const toUpdate: UpdatePostRequest[] = []
    const toDelete: number[] = []

    if (state.profile.posts) {
      for (let i = 0; i < state.profile.posts.length; i += 1) {
        if (!(state.profile.posts[i].id in attachments)) {
          toDelete.push(state.profile.posts[i].id)
        }
      }
    }

    await Promise.all(toDelete.map((id) => deletePost(id)))

    for (let i = 0; i < posts.length; i += 1) {
      const post = posts[i]

      if (post.id > 0) {
        toUpdate.push({
          id: post.id,
          body: post.text,
          attachmentIds: attachments[post.id],
          tariffIds: post.subscriptionIds,
          promotional_page_id: post.promotional_page_id || null,
          comments: post.comments
        })
      } else {
        toCreate.push({
          appId: state.app.id,
          idHash: state.app.idHash,
          body: post.text,
          attachmentIds: attachments[post.id],
          tariffIds: post.subscriptionIds,
          promotional_page_id: post.promotional_page_id || null,
          comments: post.comments
        })
      }
    }

    const result = await Promise.all([
      ...toUpdate.map((r) => updatePost(r)),
      ...toCreate.map((r) => createPost(r)),
    ])
    commit('setProfile', { ...state.profile, posts: result.map(mapPost) })
  },

  async mutateChannel(
    { commit, state },
    payload: BuilderMutateChannelActionPayload
  ): Promise<void> {
    if (!state.app) {
      return
    }

    const image = payload.image ? await uploadMediaObject(payload.image) : null
    let channelResult = {}
    if (payload.id && payload.id > 0) {
      if(payload.isPersonal) {
        // Для персонального чата особое условие, https://supsterme.atlassian.net/browse/SUP-2472
        const appResult = await updateAppPersonalChat({
          id: state.app.id,
          personalChatName: payload.name,
          personalChatAvatar: image ? image : undefined,
        })
        channelResult = {
          id : 1,
          name: appResult.personalChatName,
          image: appResult.personalChatAvatar
        }
      } else {
        channelResult = await updateChannel({
          id: payload.id,
          name: payload.name,
          // tariffIds: payload.tariffIds,
          promotional_page_id: payload.promotional_page_id ?? undefined,
          imageId: image ? image.id : undefined,
        })
      }

      commit('setChannels', channelResult)
      return
    } else if (payload.name) {
      
      if(payload.isPersonal) {
        // Для персонального чата особое условие, https://supsterme.atlassian.net/browse/SUP-2472
        const appResult = await updateAppPersonalChat({
          id: state.app.id,
          personalChatName: payload.name
        })
        channelResult = {
          id : 1,
          name: appResult.personalChatName,
        }
      } else {
        channelResult = await createChannel({
          appId: state.app.id,
          idHash: state.app.idHash,
          type: payload.type,
          name: payload.name,
          promotional_page_id: payload.promotional_page_id ?? undefined,
          // tariffIds: payload.tariffIds,
          imageId: image ? image.id : undefined,
        })
      }
      commit('setChannels', [channelResult])
    }
  },
  
  async deleteChat({ commit, state }, payload: BuilderDeleteChannelActionPayload): Promise<void> {
    await deleteChannel({ id: payload.id })

    commit(
      'setChannels',
      state.channels.filter(({ id }) => id !== payload.id)
    )
  },

  async createPromotionalPage({commit}, payload: PromotionalPageRequest): Promise<void> {
    const res = await createPromotionalPage(payload)
    commit('setPromotionalPage', res)
  },

  async updatePromotionalPage({commit}, payload: PromotionalPageRequestUpdate): Promise<void> {
    const res = await updatePromotionalPage(payload)
    commit('setPromotionalPage', res)
  }
}

export default actions
